import type { PlatformSupportConversationFieldsFragment as Conversation } from '@web-app/generated/platform/graphql';

import { BLOCK_TAGS, DROPPED_TAGS, parseMessageHtml } from './message-html';

export type SupportTab = 'inbox' | 'sent' | 'resolved';
export type SupportDirection = 'incoming' | 'outgoing';
export type SupportStatusTone =
  | 'neutral'
  | 'brand'
  | 'warning'
  | 'error'
  | 'success';

export type SupportConversationStatus =
  | { kind: 'resolved' }
  | { kind: 'awaiting_support' }
  /** `hoursLeft` is negative once the 24-hour reply window has passed. */
  | { kind: 'needs_reply'; replyBy: number; hoursLeft: number };

export const SUPPORT_TABS: Array<{ id: SupportTab; label: string }> = [
  { id: 'inbox', label: 'Inbox' },
  { id: 'sent', label: 'Sent' },
  { id: 'resolved', label: 'Resolved' },
];

const REPLY_WINDOW_SECONDS = 24 * 60 * 60;
const SECONDS_PER_HOUR = 60 * 60;

/** Who started the conversation: support (incoming) or the provider (outgoing). */
export function conversationDirection(
  conversation: Conversation,
): SupportDirection {
  // A truncated history may lack the source message; the oldest visible one is
  // the best remaining signal.
  const initial =
    conversation.messages.find((message) => message.isInitial) ??
    conversation.messages[0];
  return initial?.fromProvider ? 'outgoing' : 'incoming';
}

/**
 * Whether the prescriber marked the conversation resolved and nothing newer arrived.
 * Intercom's CLOSED state is support's own workflow and does not count; any newer
 * message, from support or the prescriber, reopens the conversation.
 */
export function isResolvedByProvider(conversation: Conversation): boolean {
  // Messages are oldest first, so only the newest one can be newer than the resolve.
  const { resolvedAt } = conversation;
  const newest = conversation.messages.at(-1);
  return resolvedAt != null && (!newest || newest.createdAt <= resolvedAt);
}

/**
 * Inbox while support wrote last (waiting on the provider), Sent while the
 * provider did (waiting on support). A conversation moves as the thread goes on.
 */
export function conversationTab(conversation: Conversation): SupportTab {
  if (isResolvedByProvider(conversation)) return 'resolved';
  return conversation.messages.at(-1)?.fromProvider ? 'sent' : 'inbox';
}

/**
 * The reply clock starts at the first support message after the provider's last
 * message and stops when the provider replies or resolves. Reading does not stop it.
 */
export function conversationStatus(
  conversation: Conversation,
  now: number,
): SupportConversationStatus {
  if (isResolvedByProvider(conversation)) return { kind: 'resolved' };
  const { messages, resolvedAt } = conversation;
  // A resolve clears the clock like a reply: only support messages after both
  // the prescriber's last reply and the resolve start a new one.
  const handledIndex = messages.findLastIndex(
    (message) =>
      message.fromProvider ||
      (resolvedAt != null && message.createdAt <= resolvedAt),
  );
  const clockStart = messages[handledIndex + 1];
  if (!clockStart) return { kind: 'awaiting_support' };
  const replyBy = clockStart.createdAt + REPLY_WINDOW_SECONDS;
  return {
    kind: 'needs_reply',
    replyBy,
    hoursLeft: (replyBy - now / 1000) / SECONDS_PER_HOUR,
  };
}

export function statusBadge(status: SupportConversationStatus): {
  label: string;
  tone: SupportStatusTone;
} {
  if (status.kind === 'resolved') return { label: 'Resolved', tone: 'success' };
  if (status.kind === 'awaiting_support')
    return { label: 'Awaiting support', tone: 'neutral' };
  if (status.hoursLeft < 0)
    return {
      label: `Overdue ${Math.ceil(-status.hoursLeft)}h`,
      tone: 'error',
    };
  return {
    label:
      status.hoursLeft < 1
        ? 'Reply in <1h'
        : `Reply in ${Math.floor(status.hoursLeft)}h`,
    tone: status.hoursLeft <= 8 ? 'warning' : 'brand',
  };
}

/** Lower ranks need attention first: overdue, unread, needs reply, the rest. */
function attentionRank(conversation: Conversation, now: number): number {
  const status = conversationStatus(conversation, now);
  if (status.kind === 'needs_reply' && status.hoursLeft < 0) return 0;
  if (conversation.read === false) return 1;
  if (status.kind === 'needs_reply') return 2;
  return 3;
}

/** Stable: conversations with the same rank keep their incoming order. */
export function sortByAttention(
  conversations: Conversation[],
  now: number,
): Conversation[] {
  return conversations
    .map((conversation) => ({
      conversation,
      rank: attentionRank(conversation, now),
    }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ conversation }) => conversation);
}

/** Message text with block and `<br>` breaks kept as newlines. */
function messageText({ html, body }: Conversation['messages'][number]) {
  const root = parseMessageHtml(html);
  if (!root) return body;
  let text = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) text += node.textContent;
    if (!(node instanceof Element)) return;
    const name = node.tagName.toLowerCase();
    if (DROPPED_TAGS.has(name)) return;
    if (name === 'br') text += '\n';
    node.childNodes.forEach(walk);
    if (BLOCK_TAGS.has(name)) text += '\n';
  };
  root.childNodes.forEach(walk);
  return text;
}

/** The last message's first non-empty line, prefixed with who wrote it. */
export function conversationPreview(conversation: Conversation): string | null {
  const last = conversation.messages.at(-1);
  if (!last) return null;
  const firstLine =
    messageText(last)
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean) ?? '';
  return `${last.fromProvider ? 'You' : 'Support'}: ${firstLine}`;
}

/** How many conversations are waiting on the prescriber, and how many are overdue. */
export function replySummary(
  conversations: Conversation[],
  now: number,
): { waiting: number; overdue: number } {
  let waiting = 0;
  let overdue = 0;
  for (const conversation of conversations) {
    const status = conversationStatus(conversation, now);
    if (status.kind !== 'needs_reply') continue;
    waiting++;
    if (status.hoursLeft < 0) overdue++;
  }
  return { waiting, overdue };
}

export function plural(count: number, word: string) {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}
